import { useState, useMemo } from "react";
import { Plus, Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import type { CcTask, CcProject, CcExecutionPlan } from "@shared/schema";
import { TaskFormModal } from "@/components/task-form-modal";
import { Skeleton } from "@/components/ui/skeleton";

const PRIORITY_COLORS = {
  high: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
  medium: "bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-400",
  low: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400",
};

export default function ControlCentreTasks() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<CcTask | undefined>();
  
  const [filterProject, setFilterProject] = useState<string>("all");
  const [filterExecutionPlan, setFilterExecutionPlan] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterPriority, setFilterPriority] = useState<string>("all");
  const [filterCategory, setFilterCategory] = useState<string>("all");

  const { data: tasks = [], isLoading: tasksLoading } = useQuery<CcTask[]>({
    queryKey: ["/api/control-centre/tasks"],
  });

  const { data: projects = [], isLoading: projectsLoading } = useQuery<CcProject[]>({
    queryKey: ["/api/control-centre/projects"],
  });

  const { data: executionPlans = [], isLoading: executionPlansLoading } = useQuery<CcExecutionPlan[]>({
    queryKey: ["/api/control-centre/execution-plans"],
  });

  const handleAddTask = () => {
    setSelectedTask(undefined);
    setIsModalOpen(true);
  };

  const handleEditTask = (task: CcTask) => {
    setSelectedTask(task);
    setIsModalOpen(true);
  };

  const clearFilters = () => {
    setFilterProject("all");
    setFilterExecutionPlan("all");
    setFilterStatus("all");
    setFilterPriority("all");
    setFilterCategory("all");
  };

  const hasActiveFilters = filterProject !== "all" || filterExecutionPlan !== "all" || 
    filterStatus !== "all" || filterPriority !== "all" || filterCategory !== "all";

  // Get unique categories from tasks
  const categories = useMemo(() => {
    const cats = new Set(tasks.map(t => t.category).filter(Boolean));
    return Array.from(cats).sort();
  }, [tasks]);

  // Create lookup maps for project and execution plan names
  const projectMap = useMemo(() => {
    return new Map(projects.map(p => [p.id, p.projectName]));
  }, [projects]);

  const executionPlanMap = useMemo(() => {
    return new Map(executionPlans.map(ep => [ep.id, ep.planName]));
  }, [executionPlans]);

  // Filter tasks based on all active filters
  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      if (filterProject !== "all" && task.projectId !== filterProject) return false;
      if (filterExecutionPlan !== "all") {
        if (filterExecutionPlan === "none" && task.executionPlanId !== null) return false;
        if (filterExecutionPlan !== "none" && task.executionPlanId !== filterExecutionPlan) return false;
      }
      if (filterStatus !== "all" && task.status !== filterStatus) return false;
      if (filterPriority !== "all" && task.priority !== filterPriority) return false;
      if (filterCategory !== "all" && task.category !== filterCategory) return false;
      return true;
    });
  }, [tasks, filterProject, filterExecutionPlan, filterStatus, filterPriority, filterCategory]);

  const groupedTasks = useMemo(() => {
    return filteredTasks.reduce((acc, task) => {
      if (!acc[task.status]) {
        acc[task.status] = [];
      }
      acc[task.status].push(task);
      return acc;
    }, {} as Record<string, CcTask[]>);
  }, [filteredTasks]);

  const completedCount = filteredTasks.filter(t => t.status === "completed").length;
  const inProgressCount = filteredTasks.filter(t => t.status === "in_progress").length;
  const notStartedCount = filteredTasks.filter(t => t.status === "not_started").length;
  const totalCount = filteredTasks.length;

  const isLoading = tasksLoading || projectsLoading || executionPlansLoading;

  return (
    <div className="flex h-screen w-full flex-col">
      <PageHeader
        title="All Tasks"
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Project Manager" }, { label: "Tasks" }]}
        actions={
          <Button onClick={handleAddTask} data-testid="button-add-task">
            <Plus className="mr-2 h-4 w-4" />
            Add Task
          </Button>
        }
      />

      <main className="flex-1 overflow-auto bg-muted/20 p-6">
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Skeleton className="h-8 w-full" />
          </div>
        ) : (
          <div className="mx-auto max-w-7xl space-y-6">
            {/* Filters */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Filter className="h-5 w-5" />
                    Filters
                  </CardTitle>
                  {hasActiveFilters && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={clearFilters}
                      data-testid="button-clear-filters"
                    >
                      <X className="mr-2 h-4 w-4" />
                      Clear Filters
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-5">
                  <Select value={filterProject} onValueChange={setFilterProject}>
                    <SelectTrigger data-testid="filter-project">
                      <SelectValue placeholder="All Projects" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Projects</SelectItem>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.projectName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={filterExecutionPlan} onValueChange={setFilterExecutionPlan}>
                    <SelectTrigger data-testid="filter-execution-plan">
                      <SelectValue placeholder="All Plans" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Plans</SelectItem>
                      <SelectItem value="none">No Plan</SelectItem>
                      {executionPlans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {plan.planName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger data-testid="filter-status">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="not_started">Not Started</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="blocked">Blocked</SelectItem>
                      <SelectItem value="review">Review</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={filterPriority} onValueChange={setFilterPriority}>
                    <SelectTrigger data-testid="filter-priority">
                      <SelectValue placeholder="All Priorities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Priorities</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger data-testid="filter-category">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map((category) => (
                        <SelectItem key={category ?? "unknown"} value={category ?? "unknown"}>
                          {category ?? "Unknown"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Task Progress Summary */}
            <Card data-testid="card-task-summary">
              <CardHeader>
                <CardTitle>Task Summary</CardTitle>
                <div className="text-sm text-muted-foreground">
                  {totalCount} tasks • {completedCount} completed • {inProgressCount} in progress • {notStartedCount} not started
                </div>
              </CardHeader>
              <CardContent>
                {totalCount > 0 ? (
                  <div className="flex gap-2 h-2 rounded-full overflow-hidden bg-muted">
                    {completedCount > 0 && (
                      <div 
                        className="bg-green-500" 
                        style={{ width: `${(completedCount / totalCount) * 100}%` }} 
                      />
                    )}
                    {inProgressCount > 0 && (
                      <div 
                        className="bg-blue-500" 
                        style={{ width: `${(inProgressCount / totalCount) * 100}%` }} 
                      />
                    )}
                    {notStartedCount > 0 && (
                      <div 
                        className="bg-gray-400" 
                        style={{ width: `${(notStartedCount / totalCount) * 100}%` }} 
                      />
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground text-center">No tasks match the current filters</div>
                )}
              </CardContent>
            </Card>

            {/* Tasks Kanban */}
            <div className="grid gap-4 md:grid-cols-5">
              {(["not_started", "in_progress", "blocked", "review", "completed"] as const).map((status) => {
                const statusTasks = groupedTasks[status] || [];
                const statusLabel = status.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase());

              return (
                <Card key={status} data-testid={`column-${status}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-semibold">{statusLabel}</CardTitle>
                      <Badge variant="secondary">{statusTasks.length}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {statusTasks.map((task) => (
                      <Card 
                        key={task.id}
                        className="p-3 hover-elevate cursor-pointer"
                        data-testid={`card-task-${task.id}`}
                        onClick={() => handleEditTask(task)}
                      >
                          <h4 className="font-semibold text-sm mb-2">{task.taskTitle}</h4>
                          
                          <div className="space-y-1 mb-2">
                            <div className="text-xs text-muted-foreground">
                              <span className="font-medium">Project:</span> {projectMap.get(task.projectId) || "Unknown"}
                            </div>
                            {task.executionPlanId && (
                              <div className="text-xs text-muted-foreground">
                                <span className="font-medium">Plan:</span> {executionPlanMap.get(task.executionPlanId) || "Unknown"}
                              </div>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-1 mb-2">
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${PRIORITY_COLORS[task.priority]}`}
                            >
                              {task.priority}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {task.effort}
                            </Badge>
                            {task.category && (
                              <Badge variant="secondary" className="text-xs">
                                {task.category}
                              </Badge>
                            )}
                          </div>
                          {task.dueDate && (
                            <div className="text-xs text-muted-foreground">
                              Due: {task.dueDate}
                            </div>
                          )}
                        </Card>
                    ))}
                    {statusTasks.length === 0 && (
                      <div className="text-center py-8 text-sm text-muted-foreground">
                        No tasks
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
      </main>

      <TaskFormModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        task={selectedTask}
      />
    </div>
  );
}
